import {EChartsCoreOption} from 'echarts/core';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Store } from '@ngxs/store';
import {DashboardGeneticsRetrieveState} from '../../../dashboard-genetics/states/dashboard-genetics-retrieve/dashboard-genetics-retrieve.state';
import {Settings} from '../../../../conf/settings.base';
import {SubmissionState} from '../../../shared/microservices/submission/submission.state';
import {AuthState} from '../../../auth/states/auth/auth.state';
import {SNP} from '../../../dashboard-genetics/types/dashboard-genetics-general.types';
import { NgxEchartsDirective } from 'ngx-echarts';
import { DashboardViewerDataZoneGeneticsRatingComponent } from '../dashboard-viewer-data-zone-genetics-rating/dashboard-viewer-data-zone-genetics-rating.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-dashboard-viewer-data-zone-genetics',
    templateUrl: './dashboard-viewer-data-zone-genetics.component.html',
    styleUrls: ['./dashboard-viewer-data-zone-genetics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgxEchartsDirective, DashboardViewerDataZoneGeneticsRatingComponent, AsyncPipe]
})
export class DashboardViewerDataZoneGeneticsComponent implements OnInit, OnDestroy {
  public readonly report$ = this.store.select(DashboardGeneticsRetrieveState.geneticData);
  public readonly publicSubmissions$ = this.store.select(SubmissionState.publicSubmissions);
  public readonly selectedSubmission$ = this.store.select(SubmissionState.selectedSubmission);
  public readonly user$ = this.store.select(AuthState.getUser);

  chartOption: EChartsCoreOption = {
    title: {
      text: 'Hippocampal size',
      textStyle: {
        color: '#2B3357',
        fontSize: 16,
        fontWeight: 500,
        fontFamily: 'Montserrat, sans-serif'
      },
      left: 'center',
    },
    backgroundColor: '#f2f3f7',
    series: [
      {
        type: 'gauge',
        center: ['50%', '97%'],
        radius: '120%',
        startAngle: 180,
        endAngle: 0,
        min: -4,
        max: 4,
        splitNumber: 4,
        axisLine: {
          lineStyle: {
            width: 10,
            color: [
              [0.1, '#2EA3D7'],
              [0.2, '#2e9dd7'],
              [0.3, '#2e93d7'],
              [0.4, '#2e8bd7'],
              [0.5, '#2e7dd7'],
              [0.6, '#2e76d7'],
              [0.7, '#2e6fd7'],
              [0.8, '#2e67d7'],
              [0.9, '#2e60d7'],
              [1, '#2e4fd7'],
            ],
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          length: 10,
          lineStyle: {
            width: 3,
            color: '#2B3357'
          }
        },
        axisLabel: {
          distance: 16,
          color: '#535461',
          fontSize: 16,
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500,
          formatter: value => {
            if (value === 0) {
              return '0';
            }
            return '';
          },
        },
        anchor: {
          show: false,
        },
        pointer: {
          width: 6,
          length: '40%',
          offsetCenter: [0, -12],
          itemStyle: {
            color: '#2B3357',
          }
        },
        title: {
          show: false,
        },
        detail: {
          show: false,
        },
        data: [],
      },
      {
        type: 'gauge',
        center: ['50%', '97%'],
        radius: '120%',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 1,
        splitNumber: 8,
        zlevel: -1,
        axisLine: {
          lineStyle: {
            width: 1000,
            color: [
              [0.25, '#BED4EF'],
              [0.75, '#2e9dd700'],
              [1, '#B1BDF0'],
            ],
          }
        },
        axisLabel: {
          distance: -80,
          formatter: value => {
            if (value === 0.125) {
              return '{s|smaller}';
            } else if (value === 0.875) {
              return '{l|larger}';
            }
            return '';
          },
          rich: {
            s: {
              color: '#2EA3D7',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500,
              fontSize: 15
            },
            l: {
              color: '#2E4FD7',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500,
              fontSize: 15
            }
          }
        },
      }
    ]
  };

  firstDemoScanId: number = null;

  subscription = new Subscription();

  constructor(public settings: Settings, private cd: ChangeDetectorRef, private store: Store) {
  }

  ngOnInit() {
    this.subscription.add(
      this.report$.pipe(filter(r => !!r)).subscribe(report => {
        this.chartOption.series[0].data.push({value: report.all_snps_reports.zscore});
      })
    );
    this.subscription.add(
      this.publicSubmissions$.pipe(filter(x => !!x && x.length > 0)).subscribe(submissions => {
        this.firstDemoScanId = +submissions[0].id;
        this.cd.detectChanges();
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  refactorStudies(snps: SNP[]): {link: string, citation: string, score: number}[] {
    const studies: {[pubmedID: number]: {link: string, citation: string, score: number}} = {};
    for (const snp of snps) {
      if (snp.patient_score === 0 || !snp.studies?.length) {
        continue;
      }
      for (const study of snp.studies) {
        studies[study.pubmedID] = {
          link: study.link,
          citation: study.citation,
          score: snp.scientific_score,
        };
      }
    }
    return Object.keys(studies).map(pubmedID => studies[pubmedID]).sort((a, b) => {
      if (a.score > b.score) {
        return -1;
      } else if (a.score < b.score) {
        return 1;
      } else {
        return 0;
      }
    });
  }
}
